import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Chip,
  TextField, MenuItem, Button, Grid, Card, CardContent, Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PeopleIcon from "@material-ui/icons/People";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import ChatBubbleIcon from "@material-ui/icons/ChatBubble";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "26px",
    background: theme.palette.type === "dark"
      ? "radial-gradient(circle at 92% 2%, rgba(212,175,55,.10), transparent 28%), #0D0D0E"
      : "radial-gradient(circle at 92% 2%, rgba(212,175,55,.16), transparent 28%), #F7F5EF",
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: { padding: "16px" },
  },
  header: {
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(125deg, #070708 0%, #111113 58%, #2A210B 100%)",
    border: "1px solid rgba(212,175,55,.42)",
    borderRadius: "20px",
    padding: "30px 34px",
    marginBottom: "24px",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 18px 42px rgba(17,17,19,.24), inset 0 1px 0 rgba(242,214,117,.12)",
    "&:before": {
      content: '""', position: "absolute", width: 260, height: 260, borderRadius: "50%",
      right: -80, top: -150, background: "radial-gradient(circle,rgba(242,214,117,.22),transparent 68%)",
    },
    [theme.breakpoints.down("sm")]: { alignItems: "flex-start", flexDirection: "column", gap: 20, padding: "24px" },
  },
  headerCopy: { position: "relative", zIndex: 1 },
  headerEyebrow: {
    color: "#F2D675", fontSize: 10, fontWeight: 900, letterSpacing: "1.8px",
    textTransform: "uppercase", marginBottom: 7,
  },
  headerTitle: {
    fontWeight: 900,
    fontSize: "2rem",
    letterSpacing: "-0.7px",
  },
  headerSubtitle: {
    color: "rgba(255,255,255,.72)",
    fontSize: "0.9rem",
    marginTop: 5,
  },
  headerActions: {
    position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 12,
    [theme.breakpoints.down("xs")]: { width: "100%", justifyContent: "space-between" },
  },
  liveIndicator: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#F8E7A8",
    background: "rgba(212,175,55,.12)",
    border: "1px solid rgba(212,175,55,.34)",
    borderRadius: "20px",
    padding: "6px 14px",
  },
  liveDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: "#D4AF37",
    boxShadow: "0 0 0 4px rgba(212,175,55,.13)",
    animation: "$pulse 1.5s infinite",
  },
  "@keyframes pulse": {
    "0%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.5, transform: "scale(1.3)" },
    "100%": { opacity: 1, transform: "scale(1)" },
  },
  filterCard: {
    borderRadius: "16px",
    marginBottom: "22px",
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "rgba(212,175,55,.22)" : "rgba(166,124,0,.20)",
    background: theme.palette.type === "dark" ? "#151516" : "rgba(255,255,255,.92)",
    boxShadow: "0 10px 28px rgba(17,17,19,.08)",
  },
  filterCardContent: {
    padding: "22px 24px !important",
  },
  filterTitle: {
    fontWeight: 850,
    color: theme.palette.type === "dark" ? "#F2D675" : "#5B4510",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  filterHint: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#666",
    marginTop: "10px",
    fontStyle: "italic",
  },
  applyBtn: {
    minHeight: 40,
    background: "linear-gradient(135deg, #F2D675, #D4AF37)",
    color: "#171306",
    borderRadius: "10px",
    fontWeight: 850,
    boxShadow: "0 8px 18px rgba(212,175,55,.22)",
    "&:hover": { background: "linear-gradient(135deg, #F7E5A2, #C59B24)" },
  },
  sectionBar: {
    display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14,
    margin: "4px 2px 14px",
  },
  sectionTitle: { fontSize: 15, fontWeight: 900, color: theme.palette.type === "dark" ? "#F4F1E8" : "#1B1A17" },
  sectionHint: { fontSize: 12, color: theme.palette.type === "dark" ? "rgba(255,255,255,.52)" : "#7B7465" },
  summaryGrid: { marginBottom: "22px" },
  summaryCard: {
    height: "100%", position: "relative", overflow: "hidden", borderRadius: 16,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(212,175,55,.20)" : "rgba(166,124,0,.16)"}`,
    background: theme.palette.type === "dark" ? "linear-gradient(145deg,#171718,#111112)" : "rgba(255,255,255,.94)",
    boxShadow: "0 8px 22px rgba(17,17,19,.07)", transition: "transform .2s ease, box-shadow .2s ease",
    "&:before": { content: '""', position: "absolute", left: 0, top: 0, right: 0, height: 3, background: "linear-gradient(90deg,#8A6809,#F2D675,#8A6809)" },
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 14px 28px rgba(17,17,19,.11)" },
  },
  summaryContent: { padding: "18px 20px !important" },
  summaryTop: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 },
  summaryIcon: {
    display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34,
    borderRadius: 10, color: "#8A6809", background: "rgba(212,175,55,.13)",
  },
  summaryLabel: { fontSize: "0.72rem", color: theme.palette.type === "dark" ? "#BDB7A8" : "#756E60", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".6px" },
  summaryValue: { fontSize: "2rem", fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums" },
  tableCard: {
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 12px 30px rgba(17,17,19,.09)",
    border: "1px solid",
    borderColor: theme.palette.type === "dark" ? "rgba(212,175,55,.20)" : "rgba(166,124,0,.16)",
    background: theme.palette.type === "dark" ? "#151516" : "#fff",
  },
  tableHead: {
    background: "linear-gradient(115deg, #09090A 0%, #181715 68%, #2A210B 100%)",
    "& .MuiTableCell-head": {
      color: "#F2D675",
      fontWeight: 700,
      fontSize: "0.78rem",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableRow: {
    transition: "background 0.2s",
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(212,175,55,.08)" : "rgba(212,175,55,.07)",
    },
    "& .MuiTableCell-root": {
      padding: "14px 16px",
      borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"}`,
      fontSize: "0.875rem",
    },
  },
  agentName: {
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#fff" : "#28251D",
    fontSize: "0.95rem",
  },

  refreshBtn: {
    borderRadius: "8px",
    borderColor: "rgba(242,214,117,.58)",
    color: "#F2D675",
    fontWeight: 600,
    "&:hover": { borderColor: "#F2D675", background: "rgba(212,175,55,.10)" },
  },
  lastUpdate: {
    opacity: 0.6,
    fontSize: "0.78rem",
    marginTop: "4px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#888",
  },
  legend: { marginTop: 16, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" },
  legendItem: { display: "flex", alignItems: "center", gap: 7 },
}));



const LiveControl = () => {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({ unassignedWaiting: 0, oldestUnassignedMinutes: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [startHour, setStartHour] = useState("8");
  const [endHour, setEndHour] = useState("18");
  const [appliedHours, setAppliedHours] = useState({ start: "0", end: "23" });

  const fetchData = useCallback(async () => {
    try {
      const { data: result } = await api.get("/performance/live", {
        params: { startHour: appliedHours.start, endHour: appliedHours.end },
      });
      setData(Array.isArray(result) ? result : (result.users || []));
      setSummary(Array.isArray(result) ? { unassignedWaiting: 0, oldestUnassignedMinutes: 0 } : (result.summary || {}));
      setLastUpdate(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [appliedHours]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const applyFilter = () => {
    setAppliedHours({ start: startHour, end: endHour });
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  const assignedWaiting = data.reduce((s, u) => s + parseInt(u.waitingCount || 0), 0);
  const totalWaiting = assignedWaiting + parseInt(summary.unassignedWaiting || 0);
  const totalActive = data.reduce((s, u) => s + parseInt(u.activeCount || 0), 0);
  const totalToday = data.reduce((s, u) => s + parseInt(u.todayCount || 0), 0);
  const onlineAgents = data.filter(u => u.online === true).length;

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerCopy}>
          <Typography className={classes.headerEyebrow}>
            Estratiza Hub · Central de operações
          </Typography>
          <Typography className={classes.headerTitle}>
            Controle ao Vivo
          </Typography>
          <Typography className={classes.headerSubtitle}>
            Monitoramento em tempo real dos atendentes
          </Typography>
          {lastUpdate && (
            <Typography className={classes.lastUpdate}>
              Atualizado às {lastUpdate.toLocaleTimeString("pt-BR")} · atualiza a cada 30s
            </Typography>
          )}
        </Box>
        <Box className={classes.headerActions}>
          <Box className={classes.liveIndicator}>
            <Box className={classes.liveDot} />
            <Typography style={{ color: "inherit", fontWeight: 800, fontSize: "0.8rem", letterSpacing: ".5px" }}>
              AO VIVO
            </Typography>
          </Box>
          <Button
            variant="outlined"
            className={classes.refreshBtn}
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            size="small"
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      {/* Summary cards */}
      <Box className={classes.sectionBar}>
        <Box>
          <Typography className={classes.sectionTitle}>Panorama operacional</Typography>
          <Typography className={classes.sectionHint}>Indicadores instantâneos da equipe e da fila</Typography>
        </Box>
      </Box>
      <Grid container spacing={2} className={classes.summaryGrid}>
        {[
          { label: "Online agora", value: onlineAgents, icon: <PeopleIcon />, color: "#A67C00" },
          { label: "Aguardando agora", value: totalWaiting, icon: <HourglassEmptyIcon />, color: totalWaiting > 5 ? "#c62828" : "#A67C00" },
          { label: "Sem atendente", value: summary.unassignedWaiting || 0, icon: <HourglassEmptyIcon />, color: "#c62828" },
          { label: "Abertos atribuídos", value: totalActive, icon: <ChatBubbleIcon />, color: "#8A6809" },
          { label: "Atendimentos hoje", value: totalToday, icon: <AccessTimeIcon />, color: "#D4AF37" },
        ].map((card, i) => (
          <Grid item xs={6} sm key={i}>
            <Card className={classes.summaryCard}>
              <CardContent className={classes.summaryContent}>
                <Box className={classes.summaryTop}>
                  <Box className={classes.summaryIcon} style={{ color: card.color }}>{card.icon}</Box>
                  <Typography className={classes.summaryLabel}>
                    {card.label}
                  </Typography>
                </Box>
                <Typography className={classes.summaryValue} style={{ color: card.color }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filter */}
      <Card className={classes.filterCard}>
        <CardContent className={classes.filterCardContent}>
          <Typography className={classes.filterTitle}>
            <FilterListIcon fontSize="small" /> Filtro de jornada de trabalho — médias
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth size="small" label="Início da jornada"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                variant="outlined"
              >
                {hours.map(h => (
                  <MenuItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select fullWidth size="small" label="Fim da jornada"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                variant="outlined"
              >
                {hours.map(h => (
                  <MenuItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button fullWidth className={classes.applyBtn} onClick={applyFilter} variant="contained">
                Aplicar filtro
              </Button>
            </Grid>
          </Grid>
          <Typography className={classes.filterHint}>
            Filtre o horário da jornada de trabalho da sua equipe para ter médias precisas dos colaboradores durante a jornada.
          </Typography>
        </CardContent>
      </Card>

      {/* Table */}
      <Box className={classes.sectionBar}>
        <Box>
          <Typography className={classes.sectionTitle}>Desempenho por atendente</Typography>
          <Typography className={classes.sectionHint}>Fila, tempo de espera e volume consolidado</Typography>
        </Box>
      </Box>
      <Card className={classes.tableCard}>
        {loading ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: "60px" }}>
            <CircularProgress style={{ color: "#D4AF37" }} />
          </Box>
        ) : data.length === 0 ? (
          <Box className={classes.emptyState}>
            <PeopleIcon style={{ fontSize: "3rem", color: "#ccc" }} />
            <Typography style={{ marginTop: "12px", color: "#aaa" }}>
              Nenhum atendente encontrado
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>Atendente</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Tickets com status Aguardando atribuídos ao atendente">
                      <span>Aguardando</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Há quantos minutos o cliente mais antigo aguarda resposta">
                      <span>+ Antigo esperando</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Tickets em aberto atribuídos ao atendente agora">
                      <span>Abertos atribuídos</span>
                    </Tooltip>
                  </TableCell>


                  <TableCell align="center">Atendimentos hoje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => {
                  const oldest = parseFloat(row.oldestWaitingMinutes || 0);
                  const oldestColor = oldest === 0 ? "#9e9e9e" : oldest > 30 ? "#c62828" : oldest > 10 ? "#B7791F" : "#A67C00";
                  return (
                    <TableRow key={row.id} className={classes.tableRow}>
                      <TableCell>
                        <Typography className={classes.agentName}>
                          <span style={{ color: row.online ? "#D4AF37" : "#9e9e9e", marginRight: 6 }}>●</span>
                          {row.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.waitingCount}
                          size="small"
                          style={{
                            fontWeight: 700,
                            background: parseInt(row.waitingCount) > 0 ? "rgba(198,40,40,0.12)" : "rgba(158,158,158,0.1)",
                            color: parseInt(row.waitingCount) > 0 ? "#c62828" : "#9e9e9e",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography style={{ fontWeight: 700, color: oldestColor, fontSize: "0.9rem" }}>
                          {oldest > 0 ? `${Math.round(oldest)} min` : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.activeCount}
                          size="small"
                          style={{
                            fontWeight: 700,
                            background: parseInt(row.activeCount) > 0 ? "rgba(212,175,55,.14)" : "rgba(158,158,158,0.1)",
                            color: parseInt(row.activeCount) > 0 ? "#8A6809" : "#9e9e9e",
                          }}
                        />
                      </TableCell>


                      <TableCell align="center">
                        <Typography style={{ fontWeight: 800, color: "#A67C00" }}>
                          {row.todayCount}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Legend */}
      <Box className={classes.legend}>
        {[
          { color: "#8A6809", bg: "rgba(212,175,55,.14)", label: "Rápido" },
          { color: "#B7791F", bg: "rgba(183,121,31,.13)", label: "Moderado" },
          { color: "#c62828", bg: "rgba(198,40,40,0.12)", label: "Lento" },
        ].map((l) => (
          <Box key={l.label} className={classes.legendItem}>
            <span style={{ background: l.bg, color: l.color, fontWeight: 700, fontSize: "0.78rem", padding: "2px 10px", borderRadius: "12px" }}>
              {l.label}
            </span>
            <Typography style={{ fontSize: "0.75rem", color: "#888" }}>
              {l.label === "Rápido" ? "< 5 min" : l.label === "Moderado" ? "5–15 min" : "> 15 min"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default LiveControl;
